import * as React from "react"
import { useState } from "react"

import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

interface TextareaWithCounterProps extends React.ComponentProps<"textarea"> {
  maxLength?: number;
  showCharacterCount?: boolean;
}

function TextareaWithCounter({
  className,
  maxLength,
  showCharacterCount = false,
  onFocus,
  onBlur,
  onChange,
  value,
  ...props
}: TextareaWithCounterProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [charCount, setCharCount] = useState(
    typeof value === 'string' ? value.length :
    typeof props.defaultValue === 'string' ? props.defaultValue.length :
    0
  );

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(true);
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    setIsFocused(false);
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCharCount(e.target.value.length);
    onChange?.(e);
  };

  // Sync with controlled value
  React.useEffect(() => {
    if (typeof value === 'string') {
      setCharCount(value.length);
    }
  }, [value]);

  const shouldShowCount = showCharacterCount && maxLength && (isFocused || showCharacterCount);
  const isNearLimit = maxLength ? charCount >= maxLength * 0.8 : false;

  return (
    <div className="grid w-full gap-1.5">
      <Textarea
        className={className}
        maxLength={maxLength}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleChange}
        value={value}
        {...props}
      />
      {shouldShowCount && maxLength && (
        <p className={cn(
          "text-xs text-right transition-colors",
          isNearLimit ? "text-destructive font-medium" : "text-muted-foreground"
        )}>
          {charCount}/{maxLength}
        </p>
      )}
    </div>
  )
}

export { TextareaWithCounter }
